<template>
  <div class="page-container ai-usage-page">
    <portal to="page-name">Consumo de IA</portal>

    <div class="card card-page">
      <div class="card-header">
        <span class="aiu-eyebrow">Inteligência artificial</span>
        <h1 class="card-title">Consumo de IA</h1>
        <p class="aiu-subtitle">
          Minutos de transcrição de consultas desta unidade, mês a mês,
          com o detalhe por profissional.
        </p>
      </div>

      <div class="card-body">
        <div class="aiu-filters">
          <select class="form-select aiu-select-month" v-model="selectedMonth">
            <option v-for="(name, i) in monthNames" :key="i" :value="i + 1">{{ name }}</option>
          </select>

          <select class="form-select aiu-select-year" v-model="selectedYear">
            <option v-for="year in years" :key="year" :value="year">{{ year }}</option>
          </select>

          <button
            type="button"
            class="btn btn-primary aiu-search"
            :disabled="loading"
            @click="search"
          >
            <fa-icon :icon="['fal', 'search']" /> Pesquisar
          </button>

          <div class="aiu-segments">
            <button
              type="button"
              :class="{ active: groupBy === 'feature' }"
              @click="changeGroup('feature')"
            >Por recurso</button>
            <button
              type="button"
              :class="{ active: groupBy === 'professional' }"
              @click="changeGroup('professional')"
            >Por profissional</button>
          </div>
        </div>

        <div class="loading loading-lg mt-2" v-if="loading" />

        <div class="empty" v-else-if="stale">
          <div class="empty-icon">
            <fa-icon :icon="['fal', 'search']" size="2x" />
          </div>
          <p class="empty-title h6">Mês alterado</p>
          <p class="empty-subtitle">Clique em Pesquisar para ver o consumo desse período.</p>
        </div>

        <template v-else>
          <div class="aiu-summary">
            <div class="aiu-summary-icon">
              <fa-icon :icon="['fal', 'microphone']" />
            </div>

            <div class="aiu-summary-content">
              <div class="aiu-summary-head">
                <strong>Transcrição de consultas</strong>
                <span v-if="limitShown">
                  {{ transcription.used }} de {{ limitShown }} minutos usados
                </span>
                <span v-else>{{ transcription.used || 0 }} minutos usados neste mês</span>
              </div>

              <div class="aiu-bar" v-if="limitShown">
                <div class="aiu-bar-used free" :style="{ width: `${freeWidth}%` }" />
                <div
                  class="aiu-bar-used paid"
                  :style="{ left: `${freeZone}%`, width: `${paidWidth}%` }"
                />
              </div>

              <div class="aiu-summary-legend" v-if="limitShown">
                <span>
                  <i class="aiu-dot free" />
                  Grátis: {{ transcription.usedFree || 0 }} de {{ freeShown }} min
                </span>
                <span v-if="transcription.paidLimit">
                  <i class="aiu-dot paid" />
                  Pagos: {{ transcription.usedPaid || 0 }} de {{ transcription.paidLimit }} min
                </span>
                <span v-if="minutes.billableMinutes">
                  {{ minutes.billableValue | currency }} a cobrar neste mês
                </span>
              </div>

              <div class="aiu-summary-foot">
                <span v-if="transcription.limit">
                  <strong :class="{ 'text-error': exhausted }">
                    {{ transcription.remaining }}
                  </strong>
                  minutos restantes · renova na virada do mês
                </span>
                <span v-else>Sem limite definido para esta conta.</span>
              </div>
            </div>
          </div>

          <div class="aiu-cap" v-if="minutes.available">
            <div class="aiu-cap-head">
              <div class="aiu-cap-title">
                <span class="aiu-cap-icon">
                  <fa-icon :icon="['fal', 'coins']" />
                </span>
                <div>
                  <strong>Limite de minutos pagos</strong>
                  <p>
                    Você paga só o que consumir acima dos minutos grátis, até o limite definido.
                    Sem limite, a gravação para quando a franquia acaba.
                  </p>
                </div>
              </div>
              <div class="aiu-cap-price">
                {{ minutes.unitValue | currency }}
                <small>por minuto</small>
              </div>
            </div>

            <div class="aiu-cap-form">
              <div class="aiu-cap-field">
                <span class="aiu-cap-label">Limite mensal</span>
                <div class="aiu-cap-control">
                  <st-input-number-step
                    class="aiu-cap-step"
                    v-model="capInput"
                    :min-value="0"
                    :step="capStep"
                    :readonly="saving"
                  />
                  <span class="aiu-cap-unit">min</span>
                </div>
              </div>

              <span class="aiu-cap-divider" />

              <div class="aiu-cap-field">
                <span class="aiu-cap-label">Gasto máximo</span>
                <div class="aiu-cap-total">
                  <strong>{{ (capValue * minutes.unitValue) | currency }}</strong>
                  <small>por mês</small>
                </div>
              </div>

              <button
                type="button"
                class="btn btn-icon btn-icon-left btn-primary aiu-cap-save"
                :class="{ loading: saving }"
                :disabled="saving || capValue === minutes.capMinutes"
                @click="saveCap"
              >
                <fa-icon :icon="['fal', 'save']" /> Salvar limite
              </button>
            </div>

            <p class="aiu-cap-hint" v-if="capAdjusted">
              O limite é definido em múltiplos de {{ capStep }} minutos: será salvo
              <strong>{{ capValue }} min</strong>.
            </p>
            <p class="aiu-cap-hint" v-else-if="capValue !== minutes.capMinutes">
              Limite atual: <strong>{{ minutes.capMinutes }} min</strong>
              <template v-if="minutes.capMinutes">
                · até {{ minutes.maxValue | currency }} por mês
              </template>
            </p>
          </div>

          <div class="empty" v-if="items.length === 0">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'sparkles']" size="2x" />
            </div>
            <p class="empty-title h6">Nenhum consumo neste mês</p>
            <p class="empty-subtitle">
              As gravações de consulta aparecem aqui assim que forem transcritas.
            </p>
          </div>

          <div class="aiu-table" v-else>
            <div class="aiu-row aiu-row-head">
              <span>{{ groupBy === 'professional' ? 'Profissional' : 'Consumo' }}</span>
              <span>Gravações</span>
              <span>Minutos</span>
            </div>

            <div class="aiu-row" v-for="item in items" :key="item.id || item.name">
              <span class="aiu-name">{{ shortName(item.name) }}</span>
              <span>{{ item.calls }}</span>
              <span>{{ item.minutes || '—' }}</span>
            </div>

            <div class="aiu-row aiu-row-total">
              <span>Total</span>
              <span>{{ total.calls }}</span>
              <span>{{ total.minutes }}</span>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  data() {
    return {
      loading: true,
      month: moment().format('YYYY-MM'),
      selectedMonth: moment().month() + 1,
      selectedYear: moment().year(),
      stale: false,
      groupBy: 'total',
      items: [],
      total: { calls: 0, minutes: 0, fee: 0 },
      transcription: {},
      minutes: {},
      capInput: 0,
      saving: false,
    };
  },
  computed: {
    monthNames() {
      return moment.months().map(name => name.charAt(0).toUpperCase() + name.slice(1));
    },
    years() {
      const current = moment().year();
      return Array.from({ length: 6 }, (item, i) => current - i);
    },
    selected() {
      return `${this.selectedYear}-${String(this.selectedMonth).padStart(2, '0')}`;
    },
    exhausted() {
      return !!this.transcription.limit && (this.transcription.remaining || 0) <= 0;
    },
    // consumo grátis acima da franquia atual acontece quando o parâmetro muda no
    // meio do mês: a franquia exibida acompanha o que já foi usado
    freeShown() {
      return Math.max(this.transcription.freeLimit || 0, this.transcription.usedFree || 0);
    },
    limitShown() {
      return this.freeShown + (this.transcription.paidLimit || 0);
    },
    freeZone() {
      return this.widthOf(this.freeShown);
    },
    paidZone() {
      return this.widthOf(this.transcription.paidLimit || 0);
    },
    freeWidth() {
      return this.widthOf(this.transcription.usedFree || 0);
    },
    paidWidth() {
      return this.widthOf(this.transcription.usedPaid || 0);
    },
    barWidth() {
      return this.freeWidth + this.paidWidth;
    },
    nearLimit() {
      return !this.exhausted && this.barWidth >= 80;
    },
    capStep() {
      return this.minutes.step || 100;
    },
    capValue() {
      return Math.max(Math.round((this.capInput || 0) / this.capStep) * this.capStep, 0);
    },
    capAdjusted() {
      return this.capValue !== (this.capInput || 0);
    },
  },
  watch: {
    // o número na tela é sempre do mês pesquisado: mudou a seleção, some até pesquisar de novo
    selected(value) {
      this.stale = value !== this.month;
    },
  },
  mounted() {
    this.load();
    this.loadMinutes();
  },
  methods: {
    loadMinutes() {
      return this.$httpX
        .get('/customer/ai-minutes')
        .then(({ data }) => {
          this.minutes = data;
          this.capInput = data.capMinutes;
        })
        .catch(() => {});
    },
    saveCap() {
      this.saving = true;

      return this.$http
        .put('/customer/ai-minutes', { capMinutes: this.capValue })
        .then(({ data }) => {
          this.minutes = data;
          this.capInput = data.capMinutes;
          this.transcription = data.quota;
          this.$toast.success('Limite de minutos atualizado');
        })
        .catch((e) => {
          const message = e && e.response && e.response.data && e.response.data.message;
          this.$toast.show(message || 'Não foi possível salvar o limite', { type: 'error' });
        })
        .finally(() => {
          this.saving = false;
        });
    },
    search() {
      this.month = this.selected;
      this.stale = false;
      this.load();
    },
    widthOf(minutes) {
      return this.limitShown
        ? Math.min(Math.round((minutes / this.limitShown) * 100), 100)
        : 0;
    },
    shortName(name) {
      return String(name || '').replace(/^Prontuário\s*[-–]\s*/, '');
    },
    changeGroup(groupBy) {
      this.groupBy = groupBy;
      this.load();
    },
    load() {
      this.loading = true;

      return this.$httpX
        .get('/ai-usage', { params: { month: this.month, groupBy: this.groupBy } })
        .then(({ data }) => {
          this.items = data.items || [];
          this.total = data.total || { calls: 0, minutes: 0, fee: 0 };
          this.transcription = data.transcription || {};
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";

.ai-usage-page {
  .aiu-eyebrow {
    color: $primary-color;
    font-size: $font-size-xs;
    font-weight: 700;
    letter-spacing: .05rem;
    text-transform: uppercase;
  }

  .card-title {
    margin: $unit-1 0 0;
  }

  .aiu-subtitle {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin: $unit-1 0 0;
  }

  .aiu-summary {
    align-items: flex-start;
    background: $gray-color-ultra-light;
    border-radius: $border-radius;
    display: flex;
    gap: $layout-spacing-lg;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing-lg;
  }

  .aiu-summary-icon {
    align-items: center;
    background: $light-color;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
    color: $primary-color;
    display: flex;
    flex: 0 0 auto;
    height: 1.8rem;
    justify-content: center;
    width: 1.8rem;
  }

  .aiu-summary-content {
    flex: 1;
  }

  .aiu-summary-head {
    align-items: baseline;
    display: flex;
    gap: $layout-spacing;
    justify-content: space-between;

    strong {
      color: $gray-color-900;
    }

    span {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      font-variant-numeric: tabular-nums;
    }
  }

  .aiu-bar {
    background: darken($gray-color-ultra-light, 5%);
    border-radius: 1rem;
    height: .4rem;
    margin: $unit-2 0;
    overflow: hidden;
    position: relative;
  }

  .aiu-bar-used {
    bottom: 0;
    position: absolute;
    top: 0;
    transition: width .3s;

    &.free {
      background: #2FA36B;
      left: 0;
    }

    &.paid {
      background: $secondary-color;
    }
  }

  .aiu-summary-foot {
    color: $gray-color-dark;
    font-size: $font-size-sm;

    strong {
      color: $gray-color-900;
      font-variant-numeric: tabular-nums;
    }
  }

  .aiu-summary-legend {
    color: $gray-color-dark;
    display: flex;
    flex-wrap: wrap;
    font-size: $font-size-sm;
    gap: $layout-spacing-lg;
    margin-bottom: $unit-1;
  }

  .aiu-dot {
    border-radius: 50%;
    display: inline-block;
    height: .35rem;
    margin-right: $unit-1;
    width: .35rem;

    &.free {
      background: #2FA36B;
    }

    &.paid {
      background: $secondary-color;
    }
  }

  .aiu-cap {
    background: lighten($primary-color, 36%);
    border: $border-width solid lighten($primary-color, 30%);
    border-radius: $border-radius;
    margin-bottom: $layout-spacing-lg;
    padding: $layout-spacing-lg;

    .aiu-cap-head {
      align-items: flex-start;
      display: flex;
      gap: $layout-spacing-lg;
      justify-content: space-between;
    }

    .aiu-cap-title {
      align-items: flex-start;
      display: flex;
      gap: $layout-spacing;

      strong {
        color: $gray-color-900;
      }

      p {
        color: $gray-color-dark;
        font-size: $font-size-sm;
        margin: $unit-1 0 0;
        max-width: 32rem;
      }
    }

    .aiu-cap-icon {
      align-items: center;
      background: $light-color;
      border-radius: 50%;
      box-shadow: 0 1px 3px rgba(0, 0, 0, .08);
      color: $primary-color;
      display: flex;
      flex: 0 0 auto;
      height: 1.6rem;
      justify-content: center;
      width: 1.6rem;
    }

    .aiu-cap-price {
      color: $primary-color-dark;
      font-size: $font-size-lg;
      font-variant-numeric: tabular-nums;
      font-weight: 700;
      text-align: right;
      white-space: nowrap;

      small {
        color: $gray-color-dark;
        display: block;
        font-size: $font-size-xs;
        font-weight: 400;
      }
    }

    .aiu-cap-form {
      align-items: flex-start;
      background: $light-color;
      border: $border-width solid lighten($primary-color, 32%);
      border-radius: $border-radius;
      display: flex;
      flex-wrap: wrap;
      gap: $layout-spacing-lg;
      margin-top: $layout-spacing-lg;
      padding: $layout-spacing $layout-spacing-lg;
    }

    .aiu-cap-label {
      color: $gray-color-dark;
      display: block;
      font-size: $font-size-xs;
      font-weight: 600;
      letter-spacing: .02rem;
      text-transform: uppercase;
    }

    .aiu-cap-control {
      align-items: center;
      display: flex;
      gap: $unit-2;
      margin-top: $unit-1;
    }

    .aiu-cap-step {
      width: 8rem;

      .form-input {
        font-variant-numeric: tabular-nums;
      }

      .btn:focus {
        box-shadow: none;
      }
    }

    .aiu-cap-unit {
      color: $gray-color-dark;
      font-size: $font-size-sm;
    }

    .aiu-cap-total {
      align-items: baseline;
      display: flex;
      gap: $unit-1;
      height: $control-size;
      margin-top: $unit-1;
      padding-top: $unit-1;

      strong {
        color: $gray-color-900;
        font-size: $font-size-lg;
        font-variant-numeric: tabular-nums;
      }

      small {
        color: $gray-color-dark;
        font-size: $font-size-sm;
      }

    }

    .aiu-cap-divider {
      align-self: stretch;
      background: lighten($primary-color, 30%);
      flex: 0 0 auto;
      margin: 0 $layout-spacing;
      width: $border-width;
    }

    .aiu-cap-save {
      align-self: flex-end;
      margin-left: auto;
    }

    .aiu-cap-hint {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      margin: $layout-spacing 0 0;

      strong {
        color: $gray-color-900;
        font-variant-numeric: tabular-nums;
      }
    }
  }

  .aiu-filters {
    align-items: center;
    display: flex;
    gap: $layout-spacing;
    margin-bottom: $layout-spacing-lg;

    .aiu-segments {
      margin-left: auto;
    }
  }

  .aiu-search {
    svg {
      margin-right: $unit-1;
    }
  }

  .aiu-select-month {
    width: 9rem;
  }

  .aiu-select-year {
    width: 6rem;
  }

  .aiu-segments {
    display: flex;

    button {
      background: $light-color;
      border: $border-width solid $border-color;
      color: $gray-color-dark;
      cursor: pointer;
      font-size: $font-size-sm;
      padding: $unit-1 $layout-spacing;

      &:first-child {
        border-radius: $border-radius 0 0 $border-radius;
      }

      &:last-child {
        border-left: 0;
        border-radius: 0 $border-radius $border-radius 0;
      }

      &.active {
        background: $gray-color-ultra-light;
        color: $gray-color-900;
        font-weight: 600;
      }
    }
  }

  .aiu-table {
    border: $border-width solid $border-color;
    border-radius: $border-radius;
  }

  .aiu-row {
    align-items: center;
    display: grid;
    gap: $layout-spacing;
    grid-template-columns: 1fr 5rem 6rem;
    padding: $unit-2 $layout-spacing-lg;

    & + .aiu-row {
      border-top: $border-width solid $border-color;
    }

    span {
      font-variant-numeric: tabular-nums;
      text-align: right;
    }

    span:first-child {
      text-align: left;
    }

    &:not(.aiu-row-head):not(.aiu-row-total):hover {
      background: $gray-color-ultra-light;
    }
  }

  .aiu-row-head {
    background: $gray-color-ultra-light;
    color: $gray-color-dark;
    font-size: $font-size-sm;
    font-weight: 600;
  }

  .aiu-row-total {
    background: $gray-color-ultra-light;
    font-weight: 700;
  }

  .aiu-name {
    color: $gray-color-900;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}
</style>
